import { forwardRef, Popover, StyleProps } from '@chakra-ui/react';
import { PropsWithChildren, ReactElement } from 'react';

import { CalendarOutline } from '@bonusly/ds-icon';

import { useDatePicker } from '../../hooks/use-date-picker';
import { DateValue } from '../../types';
import { Calendar } from '../calendar/calendar';
import { CalendarControls } from '../calendar/calendar-controls';
import { DatePickerContext } from '../context';
import { DatePickerInput } from './date-picker-input';
import { DatePickerPopup } from './date-picker-popup';

export type DatePickerProps = PropsWithChildren<
	StyleProps & {
		isClearable?: boolean;
		disableDateIf?: (date: Date) => boolean;
		displayFormat?: 'short' | 'long';
		leftIcon?: ReactElement;
		onChange: (value: DateValue) => void;
		value: DateValue;
	}
>;

export const DatePicker = forwardRef<DatePickerProps, 'button'>(
	({ children, isClearable = true, disableDateIf, displayFormat = 'long', leftIcon = <CalendarOutline />, onChange, value, ...rest }, ref) => {
		const picker = useDatePicker({ disableDateIf, onChange, value });

		if (children) {
			return children;
		}

		return (
			<DatePickerContext
				value={{
					isClearable,
					displayFormat,
					value,
					...picker,
				}}
			>
				<Popover isLazy isOpen={picker.isOpen} onClose={picker.onClose}>
					<DatePickerInput leftIcon={leftIcon} ref={ref} {...rest} />

					<DatePickerPopup p={4}>
						<CalendarControls mb={1} />
						<Calendar mb={2} />
					</DatePickerPopup>
				</Popover>
			</DatePickerContext>
		);
	}
);

DatePicker.displayName = 'DatePicker';
